import React from 'react';
import { motion } from 'framer-motion';
import CountdownSection from './CountdownSection.tsx';
import RSVPSection from './RSVPSection.tsx';
import { ColorScheme } from '../constants/colors.ts';
import { fadeIn, slideUp, staggerChildren } from '../constants/animations.ts';
import { CardConfig } from '../constants/cards.ts';

interface MainContentProps {
  rsvpSectionRef: React.RefObject<HTMLElement | null>;
  cardConfig: CardConfig;
  colors: ColorScheme;
}

const MainContent: React.FC<MainContentProps> = ({ rsvpSectionRef, cardConfig, colors }) => {
  const { sharedDetails } = cardConfig;
  return (
    <>
      {/* Parents Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerChildren}
        className="py-20 text-center px-4 relative"
        style={{ 
          background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
        }}
      >
        {/* Decorative elements */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 opacity-20"
          style={{ borderColor: colors.accent }}
        />
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 opacity-20"
          style={{ borderColor: colors.accent }}
        />
        
        {/* Intro Section */}
        <motion.div 
          variants={staggerChildren}
          className="space-y-8 relative"
        >
          {/* Greeting */}
          <motion.div variants={slideUp} className="space-y-3">
            <motion.p 
              variants={slideUp}
              className="font-playfair text-lg mb-6"
              style={{ color: colors.textLight }}
            >
              Assalamu'alaikum & Greetings
            </motion.p>
            <motion.div variants={slideUp} className="space-y-1">
              <motion.p variants={slideUp} className="font-optima text-base uppercase tracking-wide" style={{ color: colors.text }}>Dato' Sri Dr. Mohammed Azman<br /> bin Aziz Mohammed</motion.p>
              <motion.p variants={slideUp} className="font-optima text-base uppercase" style={{ color: colors.accent }}>&</motion.p>
              <motion.p variants={slideUp} className="font-optima text-base uppercase tracking-wide" style={{ color: colors.text }}>⁠Datin Sri Wahinah<br /> binti Haroon Rasheed</motion.p>
            </motion.div>
          </motion.div>

          {/* Invitation Text */}
          <motion.div variants={slideUp} className="space-y-5">
            <motion.div variants={slideUp} className="w-16 h-[1px] mx-auto" style={{ backgroundColor: colors.accent }}></motion.div>
            <motion.p variants={slideUp} className="font-optima tracking-[0.15em] text-sm" style={{ color: colors.textLight }}>with great joy invite</motion.p>
            <motion.p variants={slideUp} className="font-optima text-base font-medium" style={{ color: colors.text }}>Tan Sri - Puan Sri - Dato' Seri - Datin Seri - Dato' - Datin - Mr. - Mrs. - Ms.</motion.p>
            <motion.p variants={slideUp} className="font-optima tracking-[0.15em] text-sm" style={{ color: colors.textLight }}>to the Sangeet Night of</motion.p>
            <motion.div variants={slideUp} className="w-16 h-[1px] mx-auto" style={{ backgroundColor: colors.accent }}></motion.div>
          </motion.div>

          {/* Couple Names */}
          <motion.div variants={slideUp} className="space-y-2 pt-2">
            <motion.p variants={slideUp} className="font-[Carly] text-4xl font-semibold leading-tight" style={{ color: colors.text }}>{sharedDetails.groomName}</motion.p>
            <motion.p variants={slideUp} className="font-optima text-lg" style={{ color: colors.accent }}>&</motion.p>
            <motion.p variants={slideUp} className="font-[Carly] text-4xl font-semibold leading-tight" style={{ color: colors.text }}>{sharedDetails.brideName}</motion.p>
          </motion.div>
          
          {/* Bride's Parents - only shown if configured */}
          {cardConfig.showBrideParents && (
            <motion.div variants={slideUp} className="space-y-3 pt-8">
              <motion.div variants={slideUp} className="w-16 h-[1px] mx-auto" style={{ backgroundColor: colors.accent }}></motion.div>
              <motion.p variants={slideUp} className="font-optima tracking-[0.15em] text-sm" style={{ color: colors.textLight }}>child of</motion.p>
              <motion.div variants={slideUp} className="space-y-1">
                <motion.p variants={slideUp} className="font-optima text-base uppercase tracking-wide" style={{ color: colors.text }}>{cardConfig.brideFatherName}</motion.p>
                <motion.p variants={slideUp} className="font-optima text-base uppercase" style={{ color: colors.accent }}>&</motion.p>
                <motion.p variants={slideUp} className="font-optima text-base uppercase tracking-wide" style={{ color: colors.text }}>{cardConfig.brideMotherName}</motion.p>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </motion.section>

      {/* Details Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerChildren}
        className="py-16 relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`,
        }}
      >            
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 opacity-20" style={{ borderColor: colors.accent }} />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 opacity-20" style={{ borderColor: colors.accent }} />
        
        <div className="relative z-10 px-4 space-y-12">
          <motion.div variants={slideUp} className="space-y-4">
            <h3 className="font-optima text-xs uppercase mb-3 text-center font-medium tracking-[0.2em]" style={{ color: colors.accent }}>Date & Time</h3>
            <div className="text-center space-y-2">
              <p className="font-optima text-lg font-medium uppercase tracking-wide" style={{ color: colors.text }}>{cardConfig.weddingDay} • {cardConfig.weddingDateDisplay}</p>
              <p className="font-optima text-lg font-medium uppercase tracking-wide" style={{ color: colors.text }}>{cardConfig.weddingTime}</p>
            </div>
          </motion.div>
          
          <motion.div variants={slideUp} className="space-y-4">
            <p className="w-12 text-center uppercase font-optima font-medium text-sm mx-auto" style={{ color: colors.accent }}>at</p>
            <h3 className="font-optima text-lg font-medium uppercase text-center tracking-wide" style={{ color: colors.text }}>{sharedDetails.venueName}</h3>
            <p className="font-optima text-sm text-center leading-relaxed" style={{ color: colors.textLight }} dangerouslySetInnerHTML={{ __html: sharedDetails.venueAddress.replace(/\n/g, '<br />') }} />
          </motion.div>

          <div className="flex justify-center">
            <img 
              src="/images/separator.png" 
              alt="Decorative separator" 
              className="h-4 w-auto opacity-80"
            />
          </div>

          {/* Dress Code Section */}
          <div className="space-y-3 text-center">
            <h3 className="font-optima text-xs uppercase mb-3 font-medium tracking-[0.2em]" style={{ color: colors.accent }}>Dress Code</h3>
            <div className="space-y-1">
              <p className="font-optima text-base tracking-wide font-medium" style={{ color: colors.text }}>{cardConfig.dressCode}</p>
            </div>
          </div>

          <div className="flex justify-center">
            <img 
              src="/images/separator.png" 
              alt="Decorative separator" 
              className="h-4 w-auto opacity-80"
            />
          </div>

          {/* <motion.div variants={slideUp} className="space-y-4">
            <h3 className="font-optima text-xs uppercase mb-3 text-center font-medium tracking-[0.2em]" style={{ color: colors.accent }}>Event Schedule</h3>
            <div className="text-center space-y-3">
              {cardConfig.schedule.map((item, index) => (
                <p key={index} className="font-optima text-base tracking-wide" style={{ color: colors.text }}>
                  {item.time} - {item.event}
                </p>
              ))}
            </div>
          </motion.div> */}
        </div>
      </motion.section>

      {/* Countdown Section */}
      <CountdownSection weddingDate={cardConfig.weddingDate} colors={colors} />

      {/* RSVP Section */}
      <RSVPSection rsvpSectionRef={rsvpSectionRef} cardConfig={cardConfig} colors={colors} />

      {/* Footer */}
      <motion.footer 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        className="py-8 mb-12 space-y-3"
        style={{ 
          background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
          color: colors.textLight 
        }}
      >
        <div className="w-12 h-[1px] mx-auto" style={{ backgroundColor: colors.accent }}></div>
        <p className="text-center text-sm opacity-80">Made with ❤️</p>
        <p className="text-center text-xs opacity-60 pb-5">© 2025 Strada. All rights reserved.</p>
      </motion.footer>
    </>
  );
};

export default MainContent;
